import React, { useEffect, useMemo, useState } from "react";
import "../../styles/VendorsAvailability.css";
import SearchIcon from "../../icons/SearchIcon";
import GloablButton from "../GloablButton";
import ArrowDownIcon from "../../icons/Arrows/ArrowDownIcon";
import StarIcon from "../../icons/StarIcon";
import FilterModal from "./FilterModal";
import OnlineIcon from "../../icons/OnlineIcon";
import OfflineIcon from "../../icons/OfflineIcon";
import vendorAvailabilityController from "../../controllers/vendorAvailabilityController";
import { userService } from "../../services/userService";
import { translateLabel, translateText, useTranslationSnapshot } from "./../../translations/runtime";


const ROWS_PER_PAGE_OPTIONS = [9, 15, 25];

const CATEGORY_TYPES = {
  Restaurants: "RESTAURANTS",
  Groceries: "GROCERIES",
  Cosmetics: "COSMETICS",
  Home: "HOME",
  Bakery: "BAKERY",
  Dairy: "DAIRY",
  Others: "OTHERS",
};

const PROMOTION_LABELS = {
  BOGO: "BOGO",
  PERCENTAGE_OFF_ITEMS: "% Off Items",
  MEAL_DEALS_FAMILY_DEALS: "Meal Deals",
  LOYALTY: "Loyalty",
};

const getStoredToken = () => {
  const storedUser = userService.getJwt();

  if (typeof storedUser === "string") return storedUser;

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const getPayload = (response) => {
  const payload = response?.data;
  return payload?.data ?? payload?.result ?? payload;
};

const getRows = (payload) => {
  if (Array.isArray(payload)) return payload;

  const rows =
    payload?.content ??
    payload?.vendors ??
    payload?.items ??
    payload?.results ??
    payload?.rows;

  return Array.isArray(rows) ? rows : [];
};

const toDisplayLabel = (value) => {
  if (!value) return "";

  return String(value)
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const normalizeStatus = (value) =>
  String(value || "").toUpperCase() === "ONLINE" ? "Online" : "Offline";

const normalizeVendor = (vendor, index) => {
  const promotionValue = Array.isArray(vendor.promotions)
    ? vendor.promotions[0]?.promotionType ||
      vendor.promotions[0]?.type ||
      vendor.promotions[0]
    : vendor.promotionType || vendor.promotion || vendor.activePromotion;

  return {
    id:
      vendor.id ??
      vendor.vendorId ??
      vendor.storeId ??
      vendor.companyId ??
      index,
    name:
      vendor.vendorName ??
      vendor.storeName ??
      vendor.name ??
      vendor.companyName ??
      "-",
    status: normalizeStatus(
      vendor.storeStatus ?? vendor.status ?? vendor.availabilityStatus,
    ),
    category: toDisplayLabel(
      vendor.categoryType ?? vendor.category ?? vendor.vendorCategory,
    ),
    city:
      vendor.region ??
      vendor.city ??
      vendor.regionName ??
      vendor.address?.city ??
      "-",
    rating:
      vendor.rating ??
      vendor.averageRating ??
      vendor.vendorRating ??
      vendor.avgRating ??
      "-",
    promotion:
      PROMOTION_LABELS[promotionValue] || toDisplayLabel(promotionValue),
    orders:
      vendor.orders ??
      vendor.orderCount ??
      vendor.totalOrders ??
      vendor.numberOfOrders ??
      0,
  };
};

const StatusBadge = ({ status }) => { useTranslationSnapshot(); return  (
  <div
    className={`vendor-status-badge vendor-status-badge-${status.toLowerCase()}`}
  >
    {status === "Online" ? <OnlineIcon /> : <OfflineIcon />}
    <div className="vendor-status-label">{status}</div>
  </div>
); };

const PROMOTION_STYLES = {
  BOGO: "vendor-promotion-badge-bogo",
  "Meal Deals": "vendor-promotion-badge-meal-deals",
  "% Off Items": "vendor-promotion-badge-off-items",
  Loyalty: "vendor-promotion-badge-loyaly",
};

const PromotionBadge = ({ promotion }) => {
  useTranslationSnapshot();
  if (!promotion) return null;
  const styleClass =
    PROMOTION_STYLES[promotion] || "vendor-promotion-badge-default";
  return (
    <div className={`vendor-promotion-badge ${styleClass}`}>{promotion}</div>
  );
};

function VendorsAvailability() {
  useTranslationSnapshot();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [page, setPage] = useState(1);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    categories: [],
    regions: [],
  });
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchVendors = async () => {
      setLoading(true);
      setError("");

      try {
        const response =
          await vendorAvailabilityController.getVendorsAvailability({
            categoryType:
              CATEGORY_TYPES[activeFilters.categories[0]] ||
              activeFilters.categories[0],
            page,
            region: activeFilters.regions[0],
            search: debouncedSearch,
            size: rowsPerPage,
            storeStatus: selectedStatus
              ? selectedStatus.toUpperCase()
              : undefined,
            jwt: getStoredToken(),
          });

        if (response?.isAxiosError || response?.response) {
          throw response;
        }

        const payload = getPayload(response);
        const rows = getRows(payload);

        if (!isCurrentRequest) return;

        setVendors(rows.map(normalizeVendor));
        setTotalRows(
          payload?.totalElements ??
            payload?.totalCount ??
            payload?.total ??
            rows.length,
        );
        setTotalPages(
          payload?.totalPages ??
            Math.ceil(
              (payload?.totalElements ??
                payload?.totalCount ??
                payload?.total ??
                rows.length) / rowsPerPage,
            ),
        );
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setVendors([]);
        setTotalRows(0);
        setTotalPages(0);
        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load vendors.",
        );
      } finally {
        if (isCurrentRequest) setLoading(false);
      }
    };

    fetchVendors();

    return () => {
      isCurrentRequest = false;
    };
  }, [
    activeFilters.categories,
    activeFilters.regions,
    debouncedSearch,
    page,
    rowsPerPage,
    selectedStatus,
  ]);

  const visiblePages = useMemo(() => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = new Set([1, totalPages, page - 1, page, page + 1]);
    return [...pages]
      .filter((pageNumber) => pageNumber >= 1 && pageNumber <= totalPages)
      .sort((a, b) => a - b);
  }, [page, totalPages]);

  return (
    <div className="vendors-availability-wrapper">
      {showFilterModal && (
        <FilterModal
          initialFilters={activeFilters}
          onClose={() => setShowFilterModal(false)}
          onApply={(filters) => {
            setActiveFilters(filters);
            setPage(1);
          }}
        />
      )}

      <div className="vendors-availability-header">
        <div className="vendors-availability-header-left">
          <div className="vendors-availability-title"> {translateLabel("txt_admin_vendors_availability", "Vendors & Availability")} </div>
          <div className="vendors-availability-subtitle">
            Understand how vendor availability affects discovery surfaces.
          </div>
        </div>
        <div className="vendors-availability-header-right">
          <div className="vendors-availability-search-box">
            <SearchIcon />
            <input
              className="vendors-availability-search-input"
              placeholder={translateLabel("txt_common_search_vendors", "Search vendors...")}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <GloablButton
            text={translateLabel("txt_admin_filter_vendors", "Filter Vendors")}
            width="308.97px"
            height="39px"
            backgroundColor="#ffffff"
            color="#344054"
            border="1px solid #D3D3D3"
            icon={<ArrowDownIcon />}
            iconPosition="right"
            onClick={() => setShowFilterModal(true)}
          />
        </div>
      </div>

      <div className="vendors-availability-table-wrapper">
        <div className="vendors-availability-table-head">
          <div className="vendors-availability-th vendors-availability-th-name"> {translateLabel("input_vendor_name", "Vendor Name")} </div>
          <div
            className="vendors-availability-th vendors-availability-th-status"
            onClick={() => setShowStatusMenu((p) => !p)}
          > {translateLabel("txt_common_status", "Status")} <ArrowDownIcon />
            {showStatusMenu && (
              <div className="vendors-status-menu">
                <div className="vendors-status-menu-label">
                  Select your Status
                </div>
                {[translateLabel("txt_admin_online", "Online"), translateLabel("txt_offline", "Offline")].map((s) => (
                  <div
                    key={s}
                    className={`vendors-status-menu-option ${selectedStatus === s ? "vendors-status-menu-option-active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedStatus(selectedStatus === s ? "" : s);
                      setShowStatusMenu(false);
                      setPage(1);
                    }}
                  >
                    {s}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="vendors-availability-th">
            Category / City <ArrowDownIcon />
          </div>
          <div className="vendors-availability-th">
            Rating <ArrowDownIcon />
          </div>
          <div className="vendors-availability-th"> {translateLabel("txt_web_vendor_promotions", "Promotions")} <ArrowDownIcon />
          </div>
          <div className="vendors-availability-th">{translateLabel("txt_end_user_orders", "Orders")}</div>
        </div>

        <div className="vendors-availability-table-body">
          {loading && (
            <div className="vendors-availability-state">Loading vendors...</div>
          )}
          {!loading && error && (
            <div className="vendors-availability-state vendors-availability-state-error">
              {translateText(error)}
            </div>
          )}
          {!loading && !error && vendors.length === 0 && (
            <div className="vendors-availability-state">No vendors found.</div>
          )}
          {!loading &&
            !error &&
            vendors.map((row) => (
              <div key={row.id} className="vendors-availability-table-row">
                <div className="vendors-availability-td vendors-availability-td-name">
                  {row.name}
                </div>
                <div className="vendors-availability-td">
                  <StatusBadge status={row.status} />
                </div>
                <div className="vendors-availability-td">
                  <div className="vendors-availability-td-category">
                    {row.category}
                  </div>
                  <div className="vendors-availability-td-city">{row.city}</div>
                </div>
                <div className="vendors-availability-td">
                  <div className="vendors-availability-rating">
                    <StarIcon />
                    {row.rating}
                  </div>
                </div>
                <div className="vendors-availability-td">
                  <PromotionBadge promotion={row.promotion} />
                </div>
                <div className="vendors-availability-td vendors-availability-td-orders">
                  {row.orders.toLocaleString()}
                </div>
              </div>
            ))}
        </div>
      </div>

      <div className="vendors-availability-pagination">
        <div className="vendors-availability-rows-per-page">
          <div className="vendors-availability-rows-label">{translateLabel("txt_common_rows_per_page", "Rows per page")}</div>
          <select
            className="vendors-availability-rows-select"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            {ROWS_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className="vendors-availability-total-rows">
          of {totalRows} rows
        </div>
        <div className="vendors-availability-page-controls">
          <GloablButton
            text={<span className="navigation-direction-icon navigation-direction-glyph">‹</span>}
            width="32px"
            height="32px"
            backgroundColor="#fff"
            color="#344054"
            border="1px solid #D3D3D3"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          />
          {visiblePages.map((pageNumber, index) => (
            <React.Fragment key={pageNumber}>
              {index > 0 && pageNumber - visiblePages[index - 1] > 1 && (
                <div className="vendors-availability-page-ellipsis">...</div>
              )}
              <GloablButton
                text={String(pageNumber)}
                width="32px"
                height="32px"
                backgroundColor={page === pageNumber ? "#FA9C03" : "#fff"}
                color={page === pageNumber ? "#fff" : "#344054"}
                border="1px solid #D3D3D3"
                onClick={() => setPage(pageNumber)}
              />
            </React.Fragment>
          ))}
          <GloablButton
            text={<span className="navigation-direction-icon navigation-direction-glyph">›</span>}
            width="32px"
            height="32px"
            backgroundColor="#fff"
            color="#344054"
            border="1px solid #D3D3D3"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={totalPages === 0 || page === totalPages || loading}
          />
        </div>
      </div>
    </div>
  );
}

export default VendorsAvailability;
